import { useMemo } from "react";
import katex from "katex";
import "katex/dist/katex.min.css";

/**
 * Renders text that may contain inline math expressions.
 *
 * Conventions:
 *   $...$    inline math (rendered with KaTeX)
 *   $$...$$  display math (block-rendered with KaTeX)
 *
 * Plain text outside math markers is rendered as-is, preserving whitespace.
 * If KaTeX fails to parse an expression (malformed LaTeX), the raw source
 * is shown unchanged with a small visual indicator — better than crashing
 * the page.
 *
 * Used by the Drill component for stimulus + option text. Items without
 * any math markers pass through unchanged.
 */

type Segment =
  | { kind: "text"; value: string }
  | { kind: "inline"; value: string }
  | { kind: "display"; value: string };

function tokenize(input: string): Segment[] {
  const segments: Segment[] = [];
  let i = 0;
  let textBuf = "";
  const flushText = () => {
    if (textBuf) {
      segments.push({ kind: "text", value: textBuf });
      textBuf = "";
    }
  };

  while (i < input.length) {
    // Display math: $$...$$
    if (input.startsWith("$$", i)) {
      const end = input.indexOf("$$", i + 2);
      if (end > i + 1) {
        flushText();
        segments.push({ kind: "display", value: input.slice(i + 2, end) });
        i = end + 2;
        continue;
      }
    }
    // Escaped currency: \$ is a literal dollar sign, never a delimiter.
    // The tokenizer has always SKIPPED \$ when hunting for a closing marker, but
    // nothing ever removed the backslash, so an author who escaped a dollar got
    // a visible "\$18" on screen and the escape was unusable. It is needed:
    // the currency guard below fails when an "=" falls between two prices, as in
    // "an $18 hold ... the displayed amount is S(d) = C(d) + 18 ... is $74",
    // where everything between the first and last price rendered as one run of
    // italic maths. Authors escape the prices; this makes that work.
    if (input[i] === "\\" && input[i + 1] === "$") {
      textBuf += "$";
      i += 2;
      continue;
    }
    // Inline math: $...$  (but not $$, and ignore escaped \$)
    // Guard against currency false positives ($5 to $10): require the
    // content between the $ marks to contain at least one LaTeX-ish
    // character (\, ^, _, =, {, }), which excludes plain prose.
    if (input[i] === "$" && input[i - 1] !== "\\") {
      let j = i + 1;
      while (j < input.length) {
        if (input[j] === "$" && input[j - 1] !== "\\") {
          break;
        }
        j++;
      }
      if (j < input.length && j > i + 1 && j - i < 200) {
        const content = input.slice(i + 1, j);
        // Treat the span as math if it has LaTeX-ish characters, OR if it
        // contains no real word (no run of 2+ letters). The second clause
        // lets simple expressions like $x$, $8+4$, $16$, $2x+6$ render while
        // still excluding prose/currency like "$5 to $10" (the "to" blocks it).
        const looksLikeMath =
          /[\\^_={}]/.test(content) || !/[A-Za-z]{2,}/.test(content);
        if (looksLikeMath) {
          flushText();
          segments.push({ kind: "inline", value: content });
          i = j + 1;
          continue;
        }
      }
    }
    textBuf += input[i];
    i++;
  }
  flushText();
  return segments;
}

/**
 * Escape HTML special characters. Every string that reaches the
 * dangerouslySetInnerHTML sink below must pass through this (or KaTeX,
 * which produces sanitized markup) — item content must never render
 * as raw HTML.
 */
function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function renderKatex(tex: string, displayMode: boolean): string {
  try {
    return katex.renderToString(tex, {
      displayMode,
      throwOnError: false,
      strict: "ignore",
      output: "html",
    });
  } catch (_err) {
    // KaTeX failed even with throwOnError:false (rare). Show raw source,
    // escaped — the raw TeX comes from item content.
    const wrapped = escapeHtml(displayMode ? `$$${tex}$$` : `$${tex}$`);
    return `<span style="color:var(--signal-red);background:rgba(255,0,0,0.08);padding:0 4px;border-radius:3px;font-family:var(--font-mono)">${wrapped}</span>`;
  }
}

export function MathText({
  text,
  as: As = "span",
  className,
  style,
}: {
  text: string;
  as?: keyof React.JSX.IntrinsicElements;
  className?: string;
  style?: React.CSSProperties;
}) {
  const html = useMemo(() => {
    if (!text || !text.includes("$")) {
      // Fast path: no math markers — still escape (this feeds
      // dangerouslySetInnerHTML); text can be null/undefined at runtime
      return text ? escapeHtml(text) : "";
    }
    const segments = tokenize(text);
    return segments
      .map((seg) => {
        if (seg.kind === "text") {
          // Escape minimal HTML to prevent injection from item content
          return escapeHtml(seg.value);
        }
        return renderKatex(seg.value, seg.kind === "display");
      })
      .join("");
  }, [text]);

  return <As className={className} style={style} dangerouslySetInnerHTML={{ __html: html }} />;
}
